import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { medicalVisitsApi, personnelApi, pdvApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Stethoscope, Plus, Calendar, AlertTriangle, CheckCircle, 
  XCircle, Clock, FileText, Upload, Download, User 
} from 'lucide-react';

const VISIT_TYPES = [
  { value: 'preventiva', label: 'Preventiva' },
  { value: 'periodica', label: 'Periodica' },
  { value: 'straordinaria', label: 'Straordinaria' },
  { value: 'preassuntiva', label: 'Preassuntiva' },
];

const OUTCOMES = [
  { value: 'idoneo', label: 'Idoneo', color: 'bg-green-500' },
  { value: 'idoneo_prescrizioni', label: 'Idoneo con prescrizioni', color: 'bg-yellow-500' },
  { value: 'non_idoneo_temporaneo', label: 'Non idoneo temporaneo', color: 'bg-orange-500' },
  { value: 'non_idoneo_permanente', label: 'Non idoneo permanente', color: 'bg-red-500' },
];

export default function MedicalVisitsPage() {
  const { user } = useAuth();
  // Solo il medico competente (oltre admin/RSPP/super_admin) può inserire/modificare visite mediche
  const canEditVisits = ['super_admin', 'admin', 'amministratore', 'rspp', 'medico_competente'].includes(user?.role);
  const [visits, setVisits] = useState([]);
  const [expiringVisits, setExpiringVisits] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [filterPdv, setFilterPdv] = useState('all');
  const [activeTab, setActiveTab] = useState('all');

  const [formData, setFormData] = useState({
    personnel_id: '',
    visit_date: '',
    visit_type: 'periodica',
    outcome: 'idoneo',
    next_visit_date: '',
    notes: '',
    prescriptions: '',
    file_url: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [visitsRes, expiringRes, personnelRes, pdvRes] = await Promise.all([
        medicalVisitsApi.getAll(),
        medicalVisitsApi.getExpiring(60),
        personnelApi.getAll(),
        pdvApi.getAll(),
      ]);
      setVisits(visitsRes.data);
      setExpiringVisits(expiringRes.data);
      setPersonnel(personnelRes.data);
      setPdvList(pdvRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      personnel_id: '',
      visit_date: '',
      visit_type: 'periodica',
      outcome: 'idoneo',
      next_visit_date: '',
      notes: '',
      prescriptions: '',
      file_url: '',
    });
    setSelectedVisit(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedVisit) {
        await medicalVisitsApi.update(selectedVisit.id, formData);
        toast.success('Visita aggiornata');
      } else {
        await medicalVisitsApi.create(formData);
        toast.success('Visita registrata');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleEdit = (visit) => {
    setSelectedVisit(visit);
    setFormData({
      personnel_id: visit.personnel_id,
      visit_date: visit.visit_date,
      visit_type: visit.visit_type,
      outcome: visit.outcome,
      next_visit_date: visit.next_visit_date || '',
      notes: visit.notes || '',
      prescriptions: visit.prescriptions || '',
      file_url: visit.file_url || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa visita?')) return;
    try {
      await medicalVisitsApi.delete(id);
      toast.success('Visita eliminata');
      loadData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  const getPersonnelName = (personnelId) => {
    const person = personnel.find(p => p.id === personnelId);
    return person ? `${person.first_name} ${person.last_name}` : 'N/D';
  };

  const getPdvName = (pdvId) => {
    const pdv = pdvList.find(p => p.id === pdvId);
    return pdv ? pdv.name : 'N/D';
  };

  const getOutcomeInfo = (outcome) => {
    return OUTCOMES.find(o => o.value === outcome) || OUTCOMES[0];
  };

  const filteredVisits = visits.filter(v => {
    if (filterPdv !== 'all') {
      const person = personnel.find(p => p.id === v.personnel_id);
      if (!person || person.pdv_id !== filterPdv) return false;
    }
    return true;
  });

  const getDaysUntilExpiry = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const today = new Date();
    return Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="medical-visits-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl">Visite Mediche</h1>
          <p className="text-muted-foreground mt-1">Gestione scadenziario visite mediche</p>
        </div>
        {canEditVisits && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-visit-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuova Visita
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{selectedVisit ? 'Modifica Visita' : 'Nuova Visita Medica'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Dipendente *</Label>
                <Select value={formData.personnel_id} onValueChange={(v) => setFormData({...formData, personnel_id: v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona dipendente" />
                  </SelectTrigger>
                  <SelectContent>
                    {personnel.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.first_name} {p.last_name} - {getPdvName(p.pdv_id)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data Visita *</Label>
                  <Input
                    type="date"
                    value={formData.visit_date}
                    onChange={(e) => setFormData({...formData, visit_date: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo Visita</Label>
                  <Select value={formData.visit_type} onValueChange={(v) => setFormData({...formData, visit_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VISIT_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Esito</Label>
                  <Select value={formData.outcome} onValueChange={(v) => setFormData({...formData, outcome: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OUTCOMES.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Prossima Visita</Label>
                  <Input
                    type="date"
                    value={formData.next_visit_date}
                    onChange={(e) => setFormData({...formData, next_visit_date: e.target.value})}
                  />
                </div>
              </div>

              {formData.outcome === 'idoneo_prescrizioni' && (
                <div className="space-y-2">
                  <Label>Prescrizioni</Label>
                  <Textarea
                    value={formData.prescriptions}
                    onChange={(e) => setFormData({...formData, prescriptions: e.target.value})}
                    placeholder="Descrivere le prescrizioni..."
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Note aggiuntive..."
                />
              </div>

              <Button type="submit" className="w-full" disabled={!formData.personnel_id || !formData.visit_date}>
                {selectedVisit ? 'Salva Modifiche' : 'Registra Visita'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Stethoscope className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{visits.length}</p>
                <p className="text-sm text-muted-foreground">Visite Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{expiringVisits.length}</p>
                <p className="text-sm text-muted-foreground">In Scadenza (60gg)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{visits.filter(v => v.outcome === 'idoneo').length}</p>
                <p className="text-sm text-muted-foreground">Idonei</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{visits.filter(v => v.outcome.includes('prescrizioni')).length}</p>
                <p className="text-sm text-muted-foreground">Con Prescrizioni</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="all">Tutte le Visite</TabsTrigger>
            <TabsTrigger value="expiring">In Scadenza</TabsTrigger>
          </TabsList>
          <Select value={filterPdv} onValueChange={setFilterPdv}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filtra per UL" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le UL</SelectItem>
              {pdvList.map(pdv => (
                <SelectItem key={pdv.id} value={pdv.id}>{pdv.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <TabsContent value="all">
          <div className="space-y-3">
            {filteredVisits.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  Nessuna visita medica registrata
                </CardContent>
              </Card>
            ) : (
              filteredVisits.map((visit) => {
                const outcomeInfo = getOutcomeInfo(visit.outcome);
                const daysUntil = getDaysUntilExpiry(visit.next_visit_date);
                return (
                  <motion.div
                    key={visit.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className="hover:shadow-md transition-shadow">
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-2 bg-muted rounded-lg">
                              <User className="w-5 h-5" />
                            </div>
                            <div>
                              <p className="font-medium">{visit.personnel_name || getPersonnelName(visit.personnel_id)}</p>
                              <p className="text-sm text-muted-foreground">
                                {visit.visit_date} - {VISIT_TYPES.find(t => t.value === visit.visit_type)?.label}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <Badge className={outcomeInfo.color}>{outcomeInfo.label}</Badge>
                            {daysUntil !== null && daysUntil <= 30 && (
                              <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                                <Clock className="w-3 h-3 mr-1" />
                                {daysUntil} giorni
                              </Badge>
                            )}
                            {canEditVisits && (
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => handleEdit(visit)}>
                                  Modifica
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => handleDelete(visit.id)}>
                                  Elimina
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        {visit.prescriptions && (
                          <p className="mt-2 text-sm text-muted-foreground">
                            <strong>Prescrizioni:</strong> {visit.prescriptions}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })
            )}
          </div>
        </TabsContent>

        <TabsContent value="expiring">
          <div className="space-y-3">
            {expiringVisits.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  Nessuna visita in scadenza nei prossimi 60 giorni
                </CardContent>
              </Card>
            ) : (
              expiringVisits.map((visit) => {
                const daysUntil = getDaysUntilExpiry(visit.next_visit_date);
                return (
                  <motion.div
                    key={visit.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <Card className={daysUntil <= 7 ? 'border-red-300 bg-red-50' : daysUntil <= 30 ? 'border-yellow-300 bg-yellow-50' : ''}>
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-lg ${daysUntil <= 7 ? 'bg-red-100' : 'bg-yellow-100'}`}>
                              <AlertTriangle className={`w-5 h-5 ${daysUntil <= 7 ? 'text-red-600' : 'text-yellow-600'}`} />
                            </div>
                            <div>
                              <p className="font-medium">{visit.personnel_name}</p>
                              <p className="text-sm text-muted-foreground">
                                Prossima visita: {visit.next_visit_date}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <Badge variant={daysUntil <= 7 ? 'destructive' : 'warning'}>
                              {daysUntil <= 0 ? 'SCADUTA' : `${daysUntil} giorni`}
                            </Badge>
                            <Button size="sm" onClick={() => {
                              setFormData({...formData, personnel_id: visit.personnel_id});
                              setDialogOpen(true);
                            }}>
                              Registra Visita
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
